import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { SnapshotOverrideCreateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type SnapshotOverrideCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  relatedEntityTypeSelectedValue?: { displayId: string; id: string } | null;
  onRelatedEntityTypeChange?: (value: string) => void;
  relatedEntityType: string;
  relatedEntityTypeError?: string;
  relatedEntityIdSelectedValue?: { displayId: string; id: string } | null;
  onRelatedEntityIdChange?: (value: string) => void;
  relatedEntityId: string;
  relatedEntityIdError?: string;
  pathSelectedValue?: { displayId: string; id: string } | null;
  onPathChange?: (value: string) => void;
  path: string;
  pathError?: string;
  valueSelectedValue?: { displayId: string; id: string } | null;
  onValueChange?: (value: string) => void;
  value: string;
  valueError?: string;
  noteSelectedValue?: { displayId: string; id: string } | null;
  onNoteChange?: (value: string) => void;
  note: string;
  noteError?: string;
  activeSelectedValue?: { displayId: string; id: string } | null;
  onActiveChange?: (value: boolean) => void;
  active: boolean;
  activeError?: string;
  snapshotOverrideCreateStore: SnapshotOverrideCreateStore;
};

export const SnapshotOverrideCreateForm: React.FC<SnapshotOverrideCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      relatedEntityType = "",
      relatedEntityTypeError,
      onRelatedEntityTypeChange = () => {},
      relatedEntityId = "",
      relatedEntityIdError,
      onRelatedEntityIdChange = () => {},
      path = "",
      pathError,
      onPathChange = () => {},
      value = "{}",
      valueError,
      onValueChange = () => {},
      note = "",
      noteError,
      onNoteChange = () => {},
      active = false,
      activeError,
      onActiveChange = () => {},
      snapshotOverrideCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="SnapshotOverride"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "relatedEntityType":
                    onRelatedEntityTypeChange?.(valor);
                    break;
                  case "relatedEntityId":
                    onRelatedEntityIdChange?.(valor);
                    break;
                  case "path":
                    onPathChange?.(valor);
                    break;
                  case "value":
                    onValueChange?.(valor);
                    break;
                  case "note":
                    onNoteChange?.(valor);
                    break;
                  case "active":
                    onActiveChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={snapshotOverrideCreateStore.getCardErrors("null")}
              title={langStore.groupSnapshotOverrideLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={relatedEntityType}
                  error={Boolean(relatedEntityTypeError)}
                  warning={
                    !relatedEntityTypeError &&
                    Boolean(
                      snapshotOverrideCreateStore.warnings["relatedEntityType"]
                    )
                  }
                  helperText={
                    relatedEntityTypeError ||
                    snapshotOverrideCreateStore.warnings["relatedEntityType"]
                      ?.message
                  }
                  label={langStore.SnapshotOverrideRelatedEntityTypeLabel}
                  onChange={(value: string) => onRelatedEntityTypeChange(value)}
                  description={
                    langStore.SnapshotOverrideRelatedEntityTypeDescription
                  }
                />

                <InputString
                  value={relatedEntityId}
                  error={Boolean(relatedEntityIdError)}
                  warning={
                    !relatedEntityIdError &&
                    Boolean(
                      snapshotOverrideCreateStore.warnings["relatedEntityId"]
                    )
                  }
                  helperText={
                    relatedEntityIdError ||
                    snapshotOverrideCreateStore.warnings["relatedEntityId"]
                      ?.message
                  }
                  label={langStore.SnapshotOverrideRelatedEntityIdLabel}
                  onChange={(value: string) => onRelatedEntityIdChange(value)}
                  description={
                    langStore.SnapshotOverrideRelatedEntityIdDescription
                  }
                />

                <InputString
                  value={path}
                  error={Boolean(pathError)}
                  warning={
                    !pathError &&
                    Boolean(snapshotOverrideCreateStore.warnings["path"])
                  }
                  helperText={
                    pathError ||
                    snapshotOverrideCreateStore.warnings["path"]?.message
                  }
                  label={langStore.SnapshotOverridePathLabel}
                  onChange={(value: string) => onPathChange(value)}
                  description={langStore.SnapshotOverridePathDescription}
                />

                <InputString
                  value={value}
                  error={Boolean(valueError)}
                  warning={
                    !valueError &&
                    Boolean(snapshotOverrideCreateStore.warnings["value"])
                  }
                  helperText={
                    valueError ||
                    snapshotOverrideCreateStore.warnings["value"]?.message
                  }
                  label={langStore.SnapshotOverrideValueLabel}
                  onChange={(value: string) => onValueChange(value)}
                  description={langStore.SnapshotOverrideValueDescription}
                />

                <InputString
                  value={note}
                  error={Boolean(noteError)}
                  warning={
                    !noteError &&
                    Boolean(snapshotOverrideCreateStore.warnings["note"])
                  }
                  helperText={
                    noteError ||
                    snapshotOverrideCreateStore.warnings["note"]?.message
                  }
                  label={langStore.SnapshotOverrideNoteLabel}
                  onChange={(value: string) => onNoteChange(value)}
                  description={langStore.SnapshotOverrideNoteDescription}
                />
                <InputBoolean
                  value={active}
                  error={Boolean(activeError)}
                  helperText={
                    activeError ||
                    snapshotOverrideCreateStore.warnings["active"]?.message
                  }
                  label={langStore.SnapshotOverrideActiveLabel}
                  description={langStore.SnapshotOverrideActiveDescription}
                  onChange={(value: boolean) => onActiveChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
